/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import ManageProperties from "@features/property-management/pages/ManagePropertiesPage";
import ManageClientsPage from "@features/client-management/pages/ManageClientsPage";
import ClientDetailPage from "@features/client-management/pages/ClientDetailPage";
import DashboardSkeleton from "@/shared/ui/components/basic/loading/DashboardSkeleton";
import {
  agencyContext, AgencyValuesType, useAgency,
} from "@pages/authenticated/agency-pages/AgenciesContext";
import { AgenciesService } from "@services/requests";
import { useLayout } from "@layout/app/LayoutContext";
import { propertyContext } from "@features/property-management/pages/PropertyContext";
import { useGlobalError } from "@context/ErrorContext";
import { AgencyVisits } from "@pages/authenticated/agency-pages";
import { fetchAgencyProperties } from "@services/housien/agencyPropertiesFetch";
import { PropertyValues } from "@pages/authenticated/agency-pages/AgenciesContext";
import DashboardPage from "@pages/authenticated/agency-pages/Dashboard";
import AgendaPage from "@features/agenda/pages/AgendaPage";
import {
  FF_APP_CALENDAR,
  FF_APP_LAUNCHES,
  FF_APP_AGENCY_SITE,
  FF_APP_PARTNERSHIPS,
  FF_APP_FUNNEL,
  FF_APP_LEASE,
  FF_APP_SALE,
  FF_APP_SPREADSHEETS,
} from "@/shared/utils/feature-flag";
import { AttendancesPage } from "@features/attendances/pages/AttendancesPage";
import { LeasesPage } from "@features/leases/pages/LeasesPage";
import { SalesPage } from "@features/sales/pages/SalesPage";
import { SaleDetailPage } from "@features/sales/pages/SaleDetailPage";
import { LeaseDetailPage } from "@features/leases/pages/LeaseDetailPage";
import {
  LaunchBoardPage,
  LaunchesListPage,
} from "@features/launches";
import PartnershipsWizardPage from "@features/partnerships/pages/PartnershipsWizardPage";
import PartnershipsHistoryPage from "@features/partnerships/pages/PartnershipsHistoryPage";
import PartnershipsSearchDetailPage from "@features/partnerships/pages/PartnershipsSearchDetailPage";
import { SettingsV2Page } from "@features/agency-settings-v2";
import { RequireAgencyPermission } from "@features/agency";
import { DealDetailPage } from "@features/deals/pages/DealDetailPage";
import { PublicationsCatalogPage } from "@features/publications-catalog/pages/PublicationsCatalogPage";
import { DashboardsListPage } from "@features/dashboards";
import { AlertsPage } from "@features/alerts";
import {
  AgencyDashboardLayout,
  AgencySiteConfigPage,
  ImportSpreadsheetPage,
  SpreadsheetsHubPage,
  SubscribePage,
} from "./App.lazy";
import { InviteTeamPage } from "@features/onboarding/pages/InviteTeamPage";
import { SubscriptionActivationGate } from "@/features/subscription";
import { LoggedPropertyRoutes } from "./App.propertyRoutes";

export { LoggedPropertyRoutes } from "./App.propertyRoutes";

/**
 * Fallback route element that redirects any unmatched agency sub-path back to
 * the agency's properties list, preserving the current `agency_id` from the
 * route params.
 */
function CatchAllRedirect() {
  const { agency_id } = useParams<RouteParams>();
  return <Navigate to={`/app/agencies/${agency_id}/properties`} replace />;
}

export type RouteParams = {
  agency_id: string;
  property_id: string;
};

/**
 * LoggedAgencyRoutes component.
 *
 * @returns {JSX.Element} React component element
 */
export const LoggedAgencyRoutes = () => {
  const navigate = useNavigate();

  const { agency_id } = useParams<RouteParams>();

  const {
    agency,
    fetchId,
    propertiesFetchId,
    propertiesFilters,
    setAgency,
    setProperties,
  } = useAgency((state) => state);

  const { setGlobalError } = useGlobalError((state) => state);

  const { updatePopupProps } = useLayout(
    (state) => state
  );

  const [skeletonReady, setSkeletonReady] = useState(false);
  const [skeletonDismissed, setSkeletonDismissed] = useState(false);

  const location = useLocation();
  const isSubscribeRoute = location.pathname.endsWith('/subscribe');
  // The invite reached from the activation card is still onboarding: the
  // buyer has never been inside the agency at that point, so the screen
  // opens on its own instead of behind the shell's entrance splash and
  // sidebar.
  const isInviteRoute = location.pathname.endsWith('/team/invite');

  useEffect(() => {
    const timer = setTimeout(() => setSkeletonReady(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (agency_id && !isSubscribeRoute) {
      AgenciesService.getAgency(agency_id)
        .then((agencyResponse) => {
          setAgency(agencyResponse as AgencyValuesType);
        })
        .catch((error) => {
          setGlobalError(error, navigate, updatePopupProps);
        });
    }
  }, [agency_id, fetchId, isSubscribeRoute]);

  useEffect(() => {
    if (agency.id) {
      fetchAgencyProperties(agency.id, { filters: propertiesFilters })
        .then((propertiesArray: PropertyValues[]) => {
          setProperties(propertiesArray);
        })
        .catch((error: any) => {
          setGlobalError(error, navigate, updatePopupProps);
        });
    }
  }, [agency.id, propertiesFetchId]);

  const { Provider: PropertyContextProvider } = propertyContext;

  const showSplash = !skeletonDismissed;
  const splashLeaving = skeletonReady && !!agency;

  return (
    <>
      {isSubscribeRoute && (
        <Routes>
          <Route path="subscribe" element={<SubscribePage />} />
        </Routes>
      )}
      {!isSubscribeRoute && !isInviteRoute && showSplash && (
        <DashboardSkeleton
          agencyName={agency?.name}
          leaving={splashLeaving}
          onExitComplete={() => setSkeletonDismissed(true)}
        />
      )}
      {/*
        The gate sits above the shell, not inside it: an agency with no
        subscription never opens, so the activation screen has to replace the
        sidebar rather than appear on top of it.

        The activation gate wraps it rather than replacing it: coming back
        from the checkout, the subscription exists at the gateway and not yet
        here -- the webhook lands seconds to minutes later -- and the plain
        gate read that gap as "no subscription" and showed the buyer the
        activation screen again, which says their payment did nothing. On
        `?step=activation` this polls the reconciliation endpoint and waits;
        every other visit falls straight through to the gate.
      */}
      {!isSubscribeRoute && agency && (
        <Suspense fallback={<DashboardSkeleton agencyName={agency?.name} />}>
          <SubscriptionActivationGate
            agency={agency}
            onActivated={(reconciled) => setAgency(reconciled as unknown as AgencyValuesType)}
          >
          <Routes>
            {/* The full-screen invite from §7. It is a screen of its own and
                not a tab, because it is reached straight from the activation
                card, before there is a team to show a tab for — and it sits
                outside the dashboard layout for the same reason: the buyer
                has not entered the agency yet, so no sidebar frames it. */}
            <Route
              path="team/invite"
              element={(
                <RequireAgencyPermission permission="invite_agency_member" redirectTo="../settings">
                  <InviteTeamPage />
                </RequireAgencyPermission>
              )}
            />
            <Route element={<AgencyDashboardLayout />}>
          <Route path="" element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          {/* Dashboards LIST page (seq-12 / DASH). Reachable from the
              right-menu "Painéis" entry the CHAT worker wires; see the
              reconciliation note in features/dashboards/index.ts. */}
          <Route path="dashboards" element={<DashboardsListPage />} />
          <Route path="properties" element={<ManageProperties />} />
          <Route path="clients" element={<ManageClientsPage />} />
          <Route path="clients/:client_id" element={<ClientDetailPage />} />
          <Route path="visits" element={<AgencyVisits />} />
          <Route path="visits/:visit_id" element={<Navigate to=".." replace />} />
          {/* Negócios folded into Atendimentos: the two areas listed the
              same opportunities and both rows opened this same detail. */}
          <Route path="deals" element={<Navigate to="../attendances" replace />} />
          <Route path="deals/:deal_id" element={<DealDetailPage />} />
          {/* The rent module is gone [Fase 7]; an old bookmark still lands somewhere. */}
          <Route path="operations/rent/*" element={<Navigate to="../dashboard" replace />} />
          {FF_APP_CALENDAR && <Route path="agenda" element={<AgendaPage />} />}
          {!FF_APP_CALENDAR && <Route path="agenda" element={<Navigate to="dashboard" replace />} />}
          {FF_APP_FUNNEL && <Route path="attendances" element={<AttendancesPage />} />}
          {!FF_APP_FUNNEL && <Route path="attendances" element={<Navigate to="dashboard" replace />} />}
          {FF_APP_LEASE && <Route path="leases" element={<LeasesPage />} />}
          {FF_APP_LEASE && <Route path="leases/:lease_id" element={<LeaseDetailPage />} />}
          {FF_APP_SALE && <Route path="sales" element={<SalesPage />} />}
          {FF_APP_SALE && <Route path="sales/:sale_id" element={<SaleDetailPage />} />}
          {!FF_APP_SALE && (
            <Route path="sales/*" element={<Navigate to="../dashboard" replace />} />
          )}
          {!FF_APP_LEASE && (
            <Route path="leases/*" element={<Navigate to="../dashboard" replace />} />
          )}
          {FF_APP_LAUNCHES && <Route path="launches" element={<LaunchesListPage />} />}
          {FF_APP_LAUNCHES && <Route path="launches/new" element={<LaunchesListPage />} />}
          {FF_APP_LAUNCHES && <Route path="launches/:launch_id" element={<LaunchBoardPage />} />}
          {!FF_APP_LAUNCHES && (
            <Route path="launches/*" element={<Navigate to="../dashboard" replace />} />
          )}
          {FF_APP_PARTNERSHIPS && (
            <>
              <Route path="partnerships" element={<PartnershipsHistoryPage />} />
              <Route path="partnerships/new" element={<PartnershipsWizardPage />} />
              <Route path="partnerships/:search_id" element={<PartnershipsSearchDetailPage />} />
            </>
          )}
          {!FF_APP_PARTNERSHIPS && <Route path="partnerships/*" element={<Navigate to="dashboard" replace />} />}
          {FF_APP_SPREADSHEETS && (
            <>
              {/* Importing is OPERATIONAL+ (`import_spreadsheet`), and so is
                  the Planilhas area, whose only section is the import
                  history. A broker typing the URL lands on the dashboard
                  instead of a screen whose every call answers 403. */}
              <Route path="spreadsheets" element={(
                <RequireAgencyPermission permission="import_spreadsheet" redirectTo="../dashboard">
                  <SpreadsheetsHubPage />
                </RequireAgencyPermission>
              )} />
              <Route path="clients/import" element={(
                <RequireAgencyPermission permission="import_spreadsheet" redirectTo="../dashboard">
                  <ImportSpreadsheetPage entity="clients" />
                </RequireAgencyPermission>
              )} />
              <Route path="properties/import" element={(
                <RequireAgencyPermission permission="import_spreadsheet" redirectTo="../dashboard">
                  <ImportSpreadsheetPage entity="properties" />
                </RequireAgencyPermission>
              )} />
            </>
          )}
          {!FF_APP_SPREADSHEETS && (
            <>
              <Route path="spreadsheets" element={<Navigate to="../dashboard" replace />} />
              <Route path="clients/import" element={<Navigate to="../dashboard" replace />} />
              <Route path="properties/import" element={<Navigate to="../dashboard" replace />} />
            </>
          )}
          {FF_APP_AGENCY_SITE && <Route path="agency-site" element={<AgencySiteConfigPage />} />}
          {!FF_APP_AGENCY_SITE && <Route path="agency-site" element={<Navigate to="dashboard" replace />} />}
          <Route
            path="settings"
            element={(
              <RequireAgencyPermission permission="view_agency_settings" redirectTo="../dashboard">
                <SettingsV2Page />
              </RequireAgencyPermission>
            )}
          />
          {/* Plano is a settings tab, not a page. The bare route rendered the
              tab's body on its own — no settings header, no tab strip, and
              none of the surface's own box — so it read as the tab opened
              broken. Everything that used to point here now lands on the
              tab itself, and the old address follows. */}
          <Route path="subscription" element={<Navigate to="../settings?section=plano" replace />} />
          <Route path="publications" element={<PublicationsCatalogPage />} />
          {/* Alertas list page — reachable from the AIDock "Alertas" entry
              (owned by the CHAT worker's seq-12 right-menu group). The
              detail path routes to the SAME list page so the assistant's
              deep link to a just-created alert (/alerts/:id) never 404s;
              the list is where an alert is managed (toggle / delete). */}
          <Route path="alerts" element={<AlertsPage />} />
          <Route path="alerts/:alert_id" element={<AlertsPage />} />
          <Route
            path="properties/:property_id/*"
            element={
              <PropertyContextProvider>
                <LoggedPropertyRoutes />
              </PropertyContextProvider>
            }
          />
        </Route>
        <Route path="*" element={<CatchAllRedirect />} />
      </Routes>
          </SubscriptionActivationGate>
    </Suspense>
      )}
    </>
  );
};
